import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import { PlatformFilterCondition } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Button, Modal } from "@mapmycustomers/ui";

import CompanyAssociation from "@app/component/associations/CompanyAssociation";
import PeopleAssociation from "@app/component/associations/PeopleAssociation";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { ManageFieldsRouteState } from "@app/scene/settings/component/ManageFields";
import { fetchNewEmailEntities } from "@app/store/email/actions";
import { isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import EmailContext from "../EmailContext";

import styles from "./EmailRecipientsAddModal.module.scss";
import InfoAlert from "./InfoAlert";
import messages from "./messages";

const ASSOCIATION_MODAL_REQUEST_FILTERS: PlatformFilterCondition[] = [
  { [CommonFieldName.EMAIL]: { [PlatformFilterOperator.NOT_EQUAL]: null } },
];
export interface AddRecipientsModalProps {
  onHide: () => void;
  onNavigated: () => void;
}

const AddRecipientsModal = ({
  isOwner,
  onEmailEntitiesFetch,
  onHide,
  onNavigated,
}: AddRecipientsModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(false);

  const { entities, entityType, onEntitiesChange } = useNonNullableContext(EmailContext);

  const emailFieldArchived = getFieldModelByEntityType(entityType).getByPlatformName(
    CommonFieldName.EMAIL
  )!.isArchived;

  const handleSelect = useCallback(
    (
      entityIds: EntitiesSupportedByEmailFeature["id"][],
      removedIds?: EntitiesSupportedByEmailFeature["id"][]
    ) => {
      const updatedEntityIds = Array.from(
        new Set([
          ...entities.map(({ id }) => id).filter((id) => !removedIds?.includes(id)),
          ...entityIds,
        ])
      );

      if (entityType) {
        setLoading(true);
        onEmailEntitiesFetch({
          callback: (entities) => {
            setLoading(false);
            onHide();
            onEntitiesChange(entities);
          },
          entityIds: updatedEntityIds,
          entityType: entityType,
        });
      }
    },
    [entities, entityType, onEmailEntitiesFetch, onHide, onEntitiesChange]
  );

  const handleGoToArchiveClick = useCallback(() => {
    history.push(`${Path.SETTINGS}/${SettingPath.FORMS_MANAGE_FIELDS}/${entityType}`, {
      archive: true,
    } as ManageFieldsRouteState);
    onHide();
    onNavigated();
  }, [history, onHide, onNavigated, entityType]);

  const title = useMemo(() => {
    switch (entityType) {
      case EntityType.COMPANY:
        return intl.formatMessage(messages.selectCompaniesTitle);
      case EntityType.PERSON:
        return intl.formatMessage(messages.selectPeopleTitle);
    }
  }, [entityType, intl]);

  const getListTitle = useCallback(
    (count: number, total: number) => intl.formatMessage(messages.listTitle, { count, total }),
    [intl]
  );

  if (emailFieldArchived) {
    return (
      <Modal onCancel={onHide} onOk={onHide} open scrollable title={title} width="41rem">
        <div className={styles.emailNotAvailableContainer}>
          <FontAwesomeIcon
            className={styles.emailNotAvailableIcon}
            icon={faExclamationCircle}
            size="1x"
          />
          <span className={styles.emailNotAvailableTitle}>
            {intl.formatMessage(messages.emailNotAvailableTitle)}
          </span>
          <span className={styles.emailNotAvailableDescription}>
            {intl.formatMessage(
              isOwner
                ? messages.emailFieldArchivedDescriptionForOwner
                : messages.emailFieldArchivedDescriptionForNonOwner
            )}
          </span>
          {isOwner && (
            <Button onClick={handleGoToArchiveClick}>
              {intl.formatMessage(messages.goToArchiveText)}
            </Button>
          )}
        </div>
      </Modal>
    );
  }

  if (entityType === EntityType.COMPANY) {
    return (
      <CompanyAssociation
        allowAdd={false}
        assignedCompanies={entities as Company[]}
        entityType={entityType}
        getListTitle={getListTitle}
        hideOnSelect={false}
        info={<InfoAlert />}
        okLoading={loading}
        okText={intl.formatMessage(messages.selectText)}
        onHide={onHide}
        onSelect={handleSelect}
        requestFilters={ASSOCIATION_MODAL_REQUEST_FILTERS}
        title={title}
      />
    );
  }

  if (entityType === EntityType.PERSON) {
    return (
      <PeopleAssociation
        allowAdd={false}
        assignedPeople={entities as Person[]}
        getListTitle={getListTitle}
        hideOnSelect={false}
        info={<InfoAlert />}
        okLoading={loading}
        okText={intl.formatMessage(messages.selectText)}
        onHide={onHide}
        onSelect={handleSelect}
        requestFilters={ASSOCIATION_MODAL_REQUEST_FILTERS}
        title={title}
      />
    );
  }
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
});

const mapDispatchToProps = {
  onEmailEntitiesFetch: fetchNewEmailEntities,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddRecipientsModal);
